import React from 'react';
import { Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Styled Waltero Typography with brand-specific font hierarchy
 */
const StyledWalteroTypography = styled(Typography)(({ theme, walteroVariant, color }) => {
  const colorStyles = {
    primary: theme.palette.waltero.primary.coreBlue,
    secondary: theme.palette.waltero.primary.mediumBlue,
    accent: theme.palette.waltero.secondary.primaryApricot,
    light: theme.palette.waltero.primary.lightBlue,
    dark: theme.palette.waltero.primary.deepBlue,
    muted: theme.palette.waltero.grey[600],
    white: theme.palette.waltero.white,
    success: theme.palette.success.main,
    warning: theme.palette.waltero.secondary.lightApricot,
    error: theme.palette.waltero.secondary.darkApricot
  };

  const variantStyles = {
    'display-large': {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 800,
      fontSize: '5rem', // 80px
      lineHeight: 1.05,
      letterSpacing: '-0.015em',
      color: colorStyles[color] || theme.palette.waltero.primary.deepBlue
    },
    
    'display-medium': {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 700,
      fontSize: '3.5rem', // 56px
      lineHeight: 1.1,
      letterSpacing: '-0.01em',
      color: colorStyles[color] || theme.palette.waltero.primary.deepBlue
    },
    
    'display-small': {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 600,
      fontSize: '2.5rem', // 40px
      lineHeight: 1.15,
      letterSpacing: '-0.005em',
      color: colorStyles[color] || theme.palette.waltero.primary.coreBlue
    },
    
    'headline-large': {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 600,
      fontSize: '2rem', // 32px
      lineHeight: 1.25,
      letterSpacing: '0em',
      color: colorStyles[color] || theme.palette.waltero.primary.coreBlue
    },
    
    'headline-medium': {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '1.75rem', // 28px
      lineHeight: 1.3,
      letterSpacing: '0em',
      color: colorStyles[color] || theme.palette.waltero.primary.coreBlue
    },
    
    'headline-small': {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '1.5rem', // 24px
      lineHeight: 1.35,
      letterSpacing: '0em',
      color: colorStyles[color] || theme.palette.waltero.primary.coreBlue
    },
    
    'title-large': {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '1.375rem', // 22px
      lineHeight: 1.4,
      letterSpacing: '0em',
      color: colorStyles[color] || theme.palette.waltero.primary.coreBlue
    },
    
    'title-medium': {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '1.125rem', // 18px
      lineHeight: 1.4,
      letterSpacing: '0em',
      color: colorStyles[color] || theme.palette.waltero.primary.coreBlue
    },
    
    'title-small': {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '1rem', // 16px
      lineHeight: 1.4,
      letterSpacing: '0.005em',
      color: colorStyles[color] || theme.palette.waltero.primary.coreBlue
    },
    
    'body-large': {
      fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '1.25rem', // 20px
      lineHeight: 1.5,
      letterSpacing: '0em',
      color: colorStyles[color] || theme.palette.waltero.primary.coreBlue
    },
    
    'body-medium': {
      fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '1rem', // 16px
      lineHeight: 1.6,
      letterSpacing: '0em',
      color: colorStyles[color] || theme.palette.waltero.primary.coreBlue
    },
    
    'body-small': {
      fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '0.875rem', // 14px
      lineHeight: 1.4,
      letterSpacing: '0em',
      color: colorStyles[color] || theme.palette.waltero.primary.mediumBlue
    },
    
    'label-large': {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '1rem', // 16px
      lineHeight: 1.2,
      letterSpacing: '0.01em',
      textTransform: 'uppercase',
      color: colorStyles[color] || theme.palette.waltero.primary.coreBlue
    },
    
    'label-medium': {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '0.875rem', // 14px
      lineHeight: 1.2,
      letterSpacing: '0.01em',
      textTransform: 'uppercase',
      color: colorStyles[color] || theme.palette.waltero.primary.coreBlue
    },
    
    'label-small': {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '0.75rem', // 12px
      lineHeight: 1.2,
      letterSpacing: '0.015em',
      textTransform: 'uppercase',
      color: colorStyles[color] || theme.palette.waltero.primary.mediumBlue
    },
    
    'caption': {
      fontFamily: '"Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 400,
      fontSize: '0.75rem', // 12px
      lineHeight: 1.3,
      letterSpacing: '0.005em',
      color: colorStyles[color] || theme.palette.waltero.grey[600]
    },
    
    'overline': {
      fontFamily: '"Mozaic Geo", "Inter", "Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: 500,
      fontSize: '0.625rem', // 10px
      lineHeight: 1.2,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      color: colorStyles[color] || theme.palette.waltero.grey[600]
    }
  };

  return variantStyles[walteroVariant] || {};
});

/**
 * WalteroTypography - Text components with brand hierarchy
 * 
 * A comprehensive typography component that implements Waltero's
 * brand typography system with proper font families, weights,
 * and color schemes.
 * 
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Text content
 * @param {string} [props.walteroVariant='body-medium'] - Typography variant following Waltero design system
 * @param {string} [props.color='primary'] - Text color ('primary', 'secondary', 'accent', 'light', 'dark', 'muted', 'white', 'success', 'warning', 'error')
 * @param {string} [props.component] - HTML element to render (defaults based on variant)
 * @param {string} [props.align='inherit'] - Text alignment
 * @param {boolean} [props.gutterBottom=false] - Whether to add bottom margin
 * @param {boolean} [props.noWrap=false] - Whether to prevent text wrapping
 * @param {boolean} [props.paragraph=false] - Whether to render as paragraph
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.sx] - Additional MUI sx props
 */
const WalteroTypography = ({
  children,
  walteroVariant = 'body-medium',
  color = 'primary',
  component,
  align = 'inherit',
  gutterBottom = false,
  noWrap = false,
  paragraph = false,
  className,
  sx,
  ...otherProps
}) => {
  const theme = useTheme();
  
  // Map Waltero variants to semantic HTML elements
  const getDefaultComponent = (variant) => {
    const componentMap = {
      'display-large': 'h1',
      'display-medium': 'h1',
      'display-small': 'h1',
      'headline-large': 'h2',
      'headline-medium': 'h3',
      'headline-small': 'h4',
      'title-large': 'h5',
      'title-medium': 'h6',
      'title-small': 'h6',
      'body-large': 'p',
      'body-medium': 'p',
      'body-small': 'p',
      'label-large': 'span',
      'label-medium': 'span',
      'label-small': 'span',
      'caption': 'span',
      'overline': 'span'
    };
    
    return componentMap[variant] || 'p';
  };
  
  const elementComponent = component || (paragraph ? 'p' : getDefaultComponent(walteroVariant));
  
  // Filter out custom props to avoid DOM warnings
  const { walteroVariant: _, color: __, ...filteredProps } = otherProps;
  
  return (
    <StyledWalteroTypography
      walteroVariant={walteroVariant}
      color={color}
      component={elementComponent}
      align={align}
      gutterBottom={gutterBottom}
      noWrap={noWrap}
      paragraph={paragraph}
      className={className}
      sx={sx}
      {...filteredProps}
    >
      {children}
    </StyledWalteroTypography>
  );
};

export default WalteroTypography;